import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Avatar } from 'ui/avatar';
import { Divider } from 'ui/divider';
import { colors, mediumShadow, spacing } from 'ui/theme';
import {
  fonts,
  subtitle1,
  body2,
  body2Bold,
  caption,
  overline,
  fontWeights,
} from 'ui/theme/typography';
import { ChevronRightSmall } from 'ui/icons/navigation/ChevronRightSmall';
import { OpenNewWindow24 } from 'ui/icons/action/OpenNewWindow24';

import { pxToRem } from 'utils/convert';

const MENU_WIDTH_PX = 250;

const MenuStyles = css`
  display: inline-flex;
  border-radius: ${spacing.half};
  font-family: ${fonts.sansSerif};
`;

const optionStyle = css`
  border-radius: ${spacing.quarter};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 0 ${spacing.half};
  text-decoration: none;
  width: 100%;

  &:hover,
  &:focus {
    background: ${colors.grey5};
  }

  :focus {
    outline-color: ${colors.purple50};
    z-index: 2;
  }
`;

export const QuickNavWrapper = styled.div`
  ${MenuStyles}
  ${mediumShadow}
  background: ${colors.white};
  z-index: 5;
  position: absolute;
`;

export const PrimaryMenuContainer = styled.div`
  ${MenuStyles}
  flex-wrap: wrap;
  width: ${pxToRem(MENU_WIDTH_PX)};
  padding: ${spacing.threeQuarter};
  ${body2Bold}
`;

export const PrimaryMenu = styled.div`
  width: 100%;
  ${body2Bold}
`;

export const PrimaryMenuHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.half};
`;

export const PrimaryMenuHeaderTitle = styled.span`
  ${subtitle1}
`;

export const StyledDivider = styled(Divider)`
  background: ${colors.white};
  border: ${spacing.single} solid ${colors.grey1};
  margin: ${spacing.half} 0;
  width: 100%;
`;

export const SubHeader = styled.span`
  /** creates font */
  ${overline}
  color: ${colors.grey50};

  display: inline-block;
  height: ${pxToRem(17)};
  margin: ${spacing.base} ${spacing.half} ${spacing.half} ${spacing.half};
  text-transform: uppercase;
`;

export const PrimaryOptionText = styled.span`
  ${body2Bold};
  display: block;
  flex: 1 1 auto;
  padding: ${spacing.half} 0;
`;

export const PrimaryOptionStyled = styled.button<{ $hover: boolean }>`
  ${optionStyle}
  /** removes inherent button styles */
  background: transparent;
  border: none;
  text-align: left;

  /** allows button to remain in hover state when selected and in secondary menu */
  ${({ $hover }) =>
    $hover &&
    css`
      background: ${colors.grey5};
    `}
`;

export const PrimaryLinkStyled = styled.a`
  ${optionStyle}
`;

export const NavigationItemsContainer = styled.div`
  padding: ${spacing.threeQuarter};
`;

// Icon Styles
export const ChevronRight = styled(ChevronRightSmall)`
  display: inline-block;
  flex: 0 0 auto;
  margin: ${spacing.half} 0;
  path {
    stroke: ${colors.grey60};
    stroke-width: 1;
  }
`;

export const OpenNewWindow = styled(OpenNewWindow24)`
  display: inline-block;
  margin: ${spacing.half} 0;
  path {
    stroke: ${colors.grey50};
  }
`;

// Secondary Nav
export const SecondaryMenu = styled.div`
  display: inline-block;
  border-radius: 0 ${spacing.half} ${spacing.half} 0;
  font-family: ${fonts.sansSerif};
  width: ${pxToRem(MENU_WIDTH_PX)};
  overflow-y: scroll;
  border-left: ${spacing.single} solid ${colors.grey5}; /** to show difference between menus */
`;

export const SecondaryOptionHeader = styled.span`
  ${body2}
`;

export const SecondaryOptionText = styled.span`
  ${caption}
  color: ${colors.textSecondary};
  font-weight: ${fontWeights.regular};
`;

export const SecondaryLinkOptionStyled = styled(Link)`
  ${optionStyle}
  flex-wrap: wrap;
  padding: ${spacing.half};
`;

export const SecondaryTagOptionStyled = styled.a`
  ${optionStyle}
  flex-wrap: wrap;
  padding: ${spacing.half};
`;

export const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

export const ProjectSelectionTitle = styled.div`
  ${body2};
  display: flex;
  align-items: center;
  .title-text {
    margin-left: 7px;
  }
`;
